import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Moon, Sun, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/i18n/LangContext";

const LangToggle = ({ compact = false }) => {
    const { lang, setLang } = useLang();
    return (
        <div className="flex items-center rounded-full border border-border p-0.5" data-testid="language-toggle" role="group" aria-label="Language / Lugha">
            {["en", "sw"].map((l) => (
                <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    aria-pressed={lang === l}
                    data-testid={`lang-toggle-${l}`}
                    className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                        lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    } ${compact ? "px-3 py-1.5" : ""}`}
                >
                    {l === "en" ? "EN" : "SW"}
                </button>
            ))}
        </div>
    );
};

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [dark, setDark] = useState(() => localStorage.getItem("hm-theme") === "dark");
    const location = useLocation();
    const { t } = useLang();

    const LINKS = [
        { to: "/", label: t.nav.home, key: "home" },
        { to: "/services", label: t.nav.services, key: "services" },
        { to: "/portfolio", label: t.nav.work, key: "work" },
        { to: "/campaigns", label: t.nav.campaigns, key: "campaigns" },
        { to: "/about", label: t.nav.about, key: "about" },
        { to: "/contact", label: t.nav.contact, key: "contact" },
    ];

    useEffect(() => {
        document.documentElement.classList.toggle("dark", dark);
        localStorage.setItem("hm-theme", dark ? "dark" : "light");
    }, [dark]);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header className="fixed inset-x-0 top-0 z-50" data-testid="navbar">
            <div className="glass-panel border-x-0 border-t-0">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
                    <Link to="/" aria-label="Hallomedics home" data-testid="nav-home-link">
                        <Logo />
                    </Link>

                    <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.key}`}
                                className={({ isActive }) =>
                                    `text-sm font-medium transition-colors duration-200 hover:text-primary dark:hover:text-teal ${
                                        isActive ? "text-primary dark:text-teal" : "text-muted-foreground"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <LangToggle />
                        <button
                            type="button"
                            onClick={() => setDark((d) => !d)}
                            aria-label={dark ? t.nav.light : t.nav.dark}
                            data-testid="theme-toggle-button"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors duration-200 hover:border-primary hover:text-primary dark:hover:border-teal dark:hover:text-teal"
                        >
                            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </button>
                        <Link
                            to="/contact"
                            data-testid="nav-cta-button"
                            className="hidden items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98] xl:inline-flex"
                        >
                            {t.nav.cta}
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpen((o) => !o)}
                            aria-label={t.nav.toggleMenu}
                            aria-expanded={open}
                            data-testid="mobile-menu-button"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground xl:hidden"
                        >
                            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <AnimatePresence>
                    {open && (
                        <motion.nav
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden border-t border-border xl:hidden"
                            aria-label="Mobile"
                            data-testid="mobile-menu"
                        >
                            <div className="flex flex-col gap-1 px-5 py-4">
                                {LINKS.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        data-testid={`mobile-nav-link-${l.key}`}
                                        className={({ isActive }) =>
                                            `rounded-lg px-3 py-2.5 text-base font-medium ${
                                                isActive ? "bg-accent text-primary dark:text-teal" : "text-muted-foreground"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}
                                <Link
                                    to="/contact"
                                    data-testid="mobile-nav-cta-button"
                                    className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                                >
                                    {t.nav.cta}
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </motion.nav>
                    )}
                </AnimatePresence>
            </div>
        </header>
    );
};
